import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiSuccessResponse, ApiErrorResponse, ApiErrorDetail, ErrorDetailType, FieldValueType } from '@shared/contracts';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

export class ApiClientError extends Error implements ApiErrorDetail {
  code: string;
  details?: ErrorDetailType;
  requestId?: string;
  timestamp?: string;

  constructor(errorDetail: ApiErrorDetail, requestId?: string, timestamp?: string) {
    super(errorDetail.message);
    this.name = 'ApiClientError';
    this.code = errorDetail.code;
    this.details = errorDetail.details;
    this.requestId = requestId;
    this.timestamp = timestamp;
  }
}

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('sensiavera_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (!config.headers['x-request-id']) {
        config.headers['x-request-id'] = `req-fe-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('sensiavera_token');
          localStorage.removeItem('sensiavera_user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private handleResponse<T>(response: AxiosResponse<ApiSuccessResponse<T>>): T {
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body && body.success === true) {
      return body.data;
    }
    return body as unknown as T;
  }

  private handleError(error: Error | AxiosError): never {
    if (axios.isAxiosError(error) && error.response?.data) {
      const errorBody = error.response.data as ApiErrorResponse;
      if (errorBody && errorBody.success === false && errorBody.error) {
        throw new ApiClientError(
          errorBody.error,
          errorBody.requestId,
          errorBody.timestamp,
        );
      }
    }

    if (error instanceof ApiClientError) {
      throw error;
    }

    const message = error.message || 'An unexpected request error occurred';
    throw new ApiClientError({
      code: 'UNKNOWN_ERROR',
      message,
    });
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.get<ApiSuccessResponse<T>>(url, config);
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  async post<T>(url: string, data?: Record<string, FieldValueType> | FormData | object, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.post<ApiSuccessResponse<T>>(url, data, config);
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  async put<T>(url: string, data?: Record<string, FieldValueType> | object, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.put<ApiSuccessResponse<T>>(url, data, config);
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  async patch<T>(url: string, data?: Record<string, FieldValueType> | object, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.patch<ApiSuccessResponse<T>>(url, data, config);
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.delete<ApiSuccessResponse<T>>(url, config);
      return this.handleResponse<T>(response);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  getRawAxios(): AxiosInstance {
    return this.axiosInstance;
  }
}

export const apiClient = new ApiClient();
